import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, BadgeCheck, Layers, Cpu, HeartHandshake } from "lucide-react";
import { FadeIn, MaskedLine, Chapter } from "@/components/shared/Reveal";
import Marquee from "@/components/shared/Marquee";
import Triad from "@/components/shared/Triad";
import Timeline from "@/components/shared/Timeline";
import CTASection from "@/components/shared/CTASection";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      ref={ref}
      data-testid="home-hero"
      className="relative min-h-screen flex items-center overflow-hidden bg-ink pt-20"
    >
      <motion.div
        style={{ y: yBg }}
        className="absolute -top-40 -right-40 w-[700px] h-[700px] rounded-full bg-amber-600/15 blur-3xl animate-pulse-glow pointer-events-none"
      />
      <div className="absolute bottom-0 left-0 w-[420px] h-[420px] rounded-full bg-brass/10 blur-3xl pointer-events-none" />
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(212,175,55,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(212,175,55,0.4) 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />
      <motion.div style={{ opacity }} className="relative mx-auto w-full max-w-[1400px] px-6 md:px-12 py-24">
        <MaskedLine delay={0.1}>
          <p className="label-mono">Nexus Intelligent Process Group, LLC</p>
        </MaskedLine>
        <h1 className="mt-8 font-serif text-5xl sm:text-6xl lg:text-8xl tracking-tighter leading-[0.95] text-slate-100">
          <MaskedLine delay={0.25}>Legacy Applications.</MaskedLine>
          <MaskedLine delay={0.4}>
            <span className="italic text-gold">Modern Possibilities.</span>
          </MaskedLine>
        </h1>
        <MaskedLine delay={0.6}>
          <p className="mt-10 max-w-xl text-lg md:text-xl text-slate-400 font-light leading-relaxed">
            From COBOL to Claude — and everything between. We modernize the systems your
            business runs on, guided by four decades of building them.
          </p>
        </MaskedLine>
        <MaskedLine delay={0.8}>
          <div className="mt-12 flex flex-wrap gap-5">
            <Link
              to="/contact"
              data-testid="hero-cta-button"
              className="group inline-flex items-center gap-3 bg-amber-400 text-ink px-8 py-4 font-mono-tech text-xs tracking-[0.22em] uppercase transition-[background-color] duration-300 hover:bg-amber-300"
            >
              Request an Assessment
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
            <Link
              to="/modernization"
              data-testid="hero-secondary-button"
              className="inline-flex items-center gap-3 border border-amber-700/50 px-8 py-4 font-mono-tech text-xs tracking-[0.22em] uppercase text-amber-400 transition-[background-color] duration-300 hover:bg-amber-500/10"
            >
              Explore Modernization
            </Link>
          </div>
        </MaskedLine>
        <MaskedLine delay={1}>
          <p className="mt-16 font-mono-tech text-[11px] tracking-[0.3em] uppercase text-slate-500">
            Est. 1980 craft // Evolution, Not Revolution
          </p>
        </MaskedLine>
      </motion.div>
    </section>
  );
};

const Home = () => {
  return (
    <main data-testid="home-page">
      <Hero />
      <Marquee />

      <section className="relative mx-auto max-w-[1400px] px-6 md:px-12 py-28 md:py-40">
        <FadeIn>
          <p className="label-mono mb-4">01 // The Practice</p>
          <h2 className="font-serif text-4xl md:text-6xl tracking-tight text-slate-100 max-w-3xl leading-tight">
            Your legacy systems are not the problem.
            <span className="italic text-amber-200/90"> Treating them like one is.</span>
          </h2>
        </FadeIn>
        <div className="mt-20 grid grid-cols-1 md:grid-cols-8 gap-8 md:gap-12">
          <FadeIn delay={0.1} className="md:col-span-3 border hairline bg-ink-light p-8 md:p-10 transition-[border-color,transform] duration-500 hover:border-amber-600/50 hover:-translate-y-1">
            <Layers className="text-gold mb-6" size={28} strokeWidth={1.5} />
            <h3 className="font-serif text-2xl md:text-3xl text-slate-100 mb-4">Decades of Business Logic</h3>
            <p className="text-slate-400 font-light leading-relaxed">
              Inside every COBOL program and aging application lives hard-won business
              knowledge. We preserve it, surface it, and carry it forward — never discard it.
            </p>
          </FadeIn>
          <FadeIn delay={0.2} className="md:col-span-5 border hairline bg-ink-light p-8 md:p-10 transition-[border-color,transform] duration-500 hover:border-amber-600/50 hover:-translate-y-1">
            <Cpu className="text-gold mb-6" size={28} strokeWidth={1.5} />
            <h3 className="font-serif text-2xl md:text-3xl text-slate-100 mb-4">The Full Technical Canvas</h3>
            <p className="text-slate-400 font-light leading-relaxed">
              Re-platform, rebuild, refactor, replace, integrate, or retire — we evaluate every
              path against your people, your data, and your budget. No predetermined technology
              recommendation. No hammer looking for nails.
            </p>
            <Link
              to="/modernization"
              data-testid="home-modernization-link"
              className="group mt-6 inline-flex items-center gap-2 font-mono-tech text-xs tracking-[0.22em] uppercase text-amber-400 hover:text-amber-300 transition-colors duration-300"
            >
              The modernization spectrum
              <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </FadeIn>
        </div>
      </section>

      <section className="relative border-t hairline bg-ink-light overflow-hidden">
        <div className="absolute top-1/3 left-1/2 w-[600px] h-[600px] -translate-x-1/2 rounded-full bg-amber-600/[0.07] blur-3xl pointer-events-none" />
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-12 py-28 md:py-40">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <FadeIn>
              <p className="label-mono mb-4">02 // The Method</p>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-slate-100 leading-tight">
                Three lenses. One right solution.
              </h2>
              <p className="mt-6 text-slate-400 font-light leading-relaxed text-lg">
                The <span className="text-amber-200">Designer</span> understands the people who
                use the system. The <span className="text-amber-200">Architect</span> finds what
                already exists worth reusing. The <span className="text-amber-200">Developer</span> evaluates
                the full technical canvas. Where the three intersect is Human-Centered Design —
                and that is where the right solution lives.
              </p>
              <Link
                to="/approach"
                data-testid="home-approach-link"
                className="group mt-8 inline-flex items-center gap-2 font-mono-tech text-xs tracking-[0.22em] uppercase text-amber-400 hover:text-amber-300 transition-colors duration-300"
              >
                Our approach
                <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </Link>
            </FadeIn>
            <Triad />
          </div>
        </div>
      </section>

      <Timeline />

      <section className="relative border-t hairline bg-ink-light">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 py-28 md:py-40 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <FadeIn className="lg:col-span-5">
            <p className="label-mono mb-4">03 // ServiceNow Credibility</p>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-slate-100 leading-tight">
              A decade deep in the platform.
            </h2>
            <p className="mt-6 text-slate-400 font-light leading-relaxed">
              More than ten years at ServiceNow as Solution Architect and Platform Developer —
              App Engine, Workflow Design Studio, configurable workspaces, UI Builder,
              dashboards, REST APIs, integrations, and rapid prototyping.
            </p>
            <p className="mt-4 text-slate-400 font-light leading-relaxed">
              And the candor that comes with mastery: not every application belongs on ServiceNow.
            </p>
          </FadeIn>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-8">
            {[
              ["Certified System Administrator", "CSA"],
              ["Certified Application Developer", "CAD"],
            ].map(([label, abbr], i) => (
              <FadeIn
                key={abbr}
                delay={0.15 * (i + 1)}
                className="border hairline bg-ink p-8 md:p-10 flex flex-col justify-between min-h-[220px] transition-[border-color,transform] duration-500 hover:border-amber-600/50 hover:-translate-y-1"
              >
                <BadgeCheck className="text-gold" size={32} strokeWidth={1.5} />
                <div>
                  <p className="font-mono-tech text-4xl text-amber-500/40 tracking-tight">{abbr}</p>
                  <p className="mt-2 font-serif text-xl text-slate-200">ServiceNow {label}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="relative border-t hairline bg-ink overflow-hidden">
        <div className="absolute -bottom-40 right-0 w-[560px] h-[560px] rounded-full bg-amber-600/10 blur-3xl pointer-events-none" />
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-12 py-28 md:py-40">
          <FadeIn className="max-w-3xl">
            <p className="label-mono mb-4">04 // The AI Horizon</p>
            <h2 className="font-serif text-4xl md:text-6xl tracking-tight text-slate-100 leading-tight">
              Modernization, <span className="italic text-gold">accelerated by AI.</span>
            </h2>
            <p className="mt-8 text-slate-400 font-light leading-relaxed text-lg">
              AI Agents, Build Agent, and AI-assisted development are not buzzwords here — they
              are daily tools. We use them to understand legacy codebases faster, prototype in
              days instead of months, and put working software in front of your people before a
              single migration decision is made.
            </p>
          </FadeIn>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              ["Understand", "AI-assisted analysis of legacy code and workflows, surfacing the business logic that documentation forgot."],
              ["Prototype", "Rapid, working prototypes on modern platforms — evaluated by real users, not slideware."],
              ["Deliver", "Build Agent and AI-assisted development compress delivery timelines without compressing quality."],
            ].map(([t, b], i) => (
              <FadeIn
                key={t}
                delay={0.12 * i}
                className="border-l-2 border-amber-700/60 pl-6 py-2"
              >
                <p className="font-mono-tech text-amber-500/70 text-xs tracking-[0.3em] mb-3">0{i + 1}</p>
                <h3 className="font-serif text-2xl text-slate-100 mb-3">{t}</h3>
                <p className="text-slate-400 font-light leading-relaxed text-sm">{b}</p>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="relative border-t hairline bg-ink-light">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 py-28 md:py-40">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <FadeIn className="lg:col-span-4">
              <div className="relative overflow-hidden border-2 border-amber-700/40">
                <img
                  src="/assets/tom-cullen.png"
                  alt="Tom Cullen, Founder of Nexus Intelligent Process Group"
                  className="h-[480px] w-full object-cover object-top heritage-image"
                />
                <div className="absolute inset-0 bg-amber-900/15 mix-blend-overlay" />
                <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-ink/70 to-transparent" />
                <span className="absolute bottom-4 left-1/2 -translate-x-1/2 font-mono-tech text-[10px] tracking-[0.3em] uppercase text-slate-200 whitespace-nowrap">
                  Tom Cullen // Founder
                </span>
              </div>
            </FadeIn>
            <FadeIn delay={0.15} className="lg:col-span-7 lg:col-start-6">
              <HeartHandshake className="text-gold mb-6" size={28} strokeWidth={1.5} />
              <p className="label-mono mb-4">05 // Founder-Led</p>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-slate-100 leading-tight">
                We still love building applications.
              </h2>
              <p className="mt-6 text-slate-400 font-light leading-relaxed text-lg">
                The founder's first production code was JCL and COBOL at Liberty Mutual in 1980.
                The tools have changed a dozen times since — IMS, CICS, DB2, VSAM, C++, Visual
                Basic, client/server, the web, Salesforce, ServiceNow, and now AI-assisted
                development. The joy of turning a business problem into working software has not.
              </p>
              <p className="mt-4 text-slate-400 font-light leading-relaxed text-lg">
                That is the difference between a consultancy that manages projects and one that
                builds solutions.
              </p>
              <Link
                to="/about"
                data-testid="home-about-link"
                className="group mt-8 inline-flex items-center gap-2 font-mono-tech text-xs tracking-[0.22em] uppercase text-amber-400 hover:text-amber-300 transition-colors duration-300"
              >
                The founder's story
                <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </Link>
            </FadeIn>
          </div>
        </div>
      </section>

      <CTASection sub="Tell us about your legacy estate. We will tell you honestly what we would do — and why." />
    </main>
  );
};

export default Home;
